import React, { useState, useCallback, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import OverviewSection from "../components/overview/OverviewSection";
import OrdersPage from "./OrdersPage";

// Constants for better maintainability
const VIEW_STATES = {
  OVERVIEW: 'overview', 
  ORDERS: 'orders'
};

const FEED_DATA = [
  {
    id: 1,
    author: "Paymeo Team",
    date: "Nov 2",
    content: "A huge Thank you for being one of the first businesses to join Paymeo. You're a pioneer! While our team is putting the finishing touches on the Paymeo mobile app for shoppers, you have a head start. You'll be the first they discover. We're flipping the script on social commerce, from endless scrolling to intent-driven connections. Thank you for being here from the beginning. Let's build.",
    likes: "24.1K",
    comments: "247",
    shares: "3",
    username: "usepaymeo",
    authorAvatar: "https://res.cloudinary.com/diml8ljwa/image/upload/v1762110634/sociallogo_ihegwo.png",
    video: "https://res.cloudinary.com/diml8ljwa/video/upload/v1762111280/thankyou_guuhg7.mp4",
  },
  {
    id: 2,
    author: "Paymeo Team",
    date: "Nov 3",
    content: "Your Mission, Should You Choose to Accept It: Build Your Catalog: Fill your store with your amazing products and services. Craft Your Story: Perfect your bio and branding so you stand out. Be Ready for Asks: Soon, a wave of users will be sending direct requests for what you offer. But for now, go make sales with Paymeo where you already do, it just got better!",
    likes: "24.1K",
    comments: "247",
    shares: "1",
    username: "usepaymeo",
    authorAvatar: "https://res.cloudinary.com/diml8ljwa/image/upload/v1762110634/sociallogo_ihegwo.png",
    image: "https://res.cloudinary.com/diml8ljwa/image/upload/v1762045743/Screenshot_394_vkowf7.png",
  }
];

const Dashboard = () => {
  const [currentView, setCurrentView] = useState(VIEW_STATES.OVERVIEW);
  const { currentUser, currentAccountDetails } = useAuth();
  const navigate = useNavigate();

  // New businesses (no product, no market, no skipped setup) go through the
  // market-selection onboarding step before landing on the dashboard.
  const hasMarketSetup = useMemo(() => {
    return currentUser?.market_setup_done > 0 || currentUser?.has_skipped_setup > 0;
  }, [currentUser]);

  // Memoized authentication check - FIXED: Added missing operator
  const shouldRedirectToSetup = useMemo(() => {
    // Users who selected a market or skipped setup (upload-product skip sets
    // has_skipped_setup too) are granted the dashboard — never force them
    // back into the complete-profile / list-first-item flow.
    if (hasMarketSetup) return false;
console.log(currentAccountDetails);
console.log(currentAccountDetails?.is_pre_paymeo, "PRE PAYMEO");
    return !currentAccountDetails?.account_number || 
           currentAccountDetails?.is_pre_paymeo > 0 ||
           (currentUser?.hasProduct === 0 && currentUser?.has_skipped_setup === 0 );
  }, [currentAccountDetails, currentUser, hasMarketSetup]);

  const shouldRedirectToMarketSetup = useMemo(() => {
    return !!currentAccountDetails?.account_number &&
           !currentAccountDetails?.is_pre_paymeo &&
           currentUser?.hasProduct === 0 &&
           !hasMarketSetup;
  }, [currentAccountDetails, currentUser, hasMarketSetup]);

  // Use useEffect for navigation side effects
  useEffect(() => {
    if (shouldRedirectToMarketSetup) {
      navigate("/setup/select-market");
    } else if (shouldRedirectToSetup) {
      navigate("/setup/complete-profile");
    }
  }, [shouldRedirectToMarketSetup, shouldRedirectToSetup, navigate]);

  // View transition handlers
  const showOrdersView = useCallback(() => {
    setCurrentView(VIEW_STATES.ORDERS);
  }, []);

  const showOverviewView = useCallback(() => {
    setCurrentView(VIEW_STATES.OVERVIEW);
  }, []);

  // Animation variants for consistent transitions
  const viewTransitionVariants = {
    overview: {
      initial: { opacity: 0, y: 40 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -40 }
    },
    orders: {
      initial: { opacity: 0, y: 100 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: 100 }
    }
  };

  // Show loading state while checking redirect, or return null if redirecting
  if (shouldRedirectToMarketSetup || shouldRedirectToSetup) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white min-h-screen w-full overflow-y-auto">
      <AnimatePresence mode="wait">
        {currentView === VIEW_STATES.OVERVIEW ? (
          <OverviewView 
            variants={viewTransitionVariants.overview}
            onShowOrders={showOrdersView}
            feedData={FEED_DATA}
          />
        ) : (
          <OrdersView 
            variants={viewTransitionVariants.orders}
            onShowOverview={showOverviewView}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Extracted Overview View Component
const OverviewView = ({ variants, onShowOrders, feedData }) => (
  <motion.div
    key="overview"
    initial={variants.initial}
    animate={variants.animate}
    exit={variants.exit}
    transition={{ duration: 0.5, ease: "easeInOut" }}
    className="w-full p-0"
  >
    <div className="container mx-auto max-w-[100vw] p-0">
      <OverviewSection feeds={feedData} />
    </div>

    <div className="hidden lg:block">
      <FloatingActionButton 
        onClick={onShowOrders}
        text="↓ Order Management"
      />
    </div>
  </motion.div>
);

// Extracted Orders View Component
const OrdersView = ({ variants, onShowOverview }) => (
  <motion.div
    key="orders"
    initial={variants.initial}
    animate={variants.animate}
    exit={variants.exit}
    transition={{ duration: 0.6, ease: "easeInOut" }}
    className="w-full p-0"
  >
    <div className="min-h-screen xl:px-5 transition lg:w-[80%] w-[100%] max-w-[100vw] m-auto">
      <BackButton 
        onClick={onShowOverview}
        text="↑ Back to Overview"
      />
      <OrdersPage />
    </div>
  </motion.div>
);

// Reusable Floating Action Button Component
const FloatingActionButton = ({ onClick, text }) => (
  <div
    onClick={onClick}
    className="fixed bottom-5 right-6 bg-blue-600 hover:bg-blue-700 transition-all duration-200 text-white px-5 py-3 rounded-full font-medium cursor-pointer shadow-lg flex items-center justify-center active:scale-95 z-50"
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === 'Enter' && onClick()}
  >
    {text}
  </div>
);

// Reusable Back Button Component
const BackButton = ({ onClick, text }) => (
  <div
    onClick={onClick}
    className="text-blue-600 text-center py-3 font-medium cursor-pointer hover:text-blue-700 transition-colors duration-200"
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === 'Enter' && onClick()}
  >
    {text}
  </div>
);

export default Dashboard;